// API service for PAYNS React application
import config from './config';
import localStorageUpload from './localStorageUpload';

const API_URL = config.API_URL;
const JWT_SECRET_KEY = 'payns-secure-jwt-key-for-authentication';

// API service object
const ApiService = {
    /**
     * Login user
     * @param {String} username - User username/NIK
     * @param {String} password - User password
     * @returns {Promise<Object>} - User data and token
     */
    login: async (username, password) => {
        try {
            // Include JWT key in the URL as a query parameter for better compatibility
            const loginUrl = `${API_URL}/auth/login?jwt_key=${encodeURIComponent(JWT_SECRET_KEY)}`;
            
            const response = await fetch(loginUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ nik: username, password })
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Login failed');
            }
            
            const data = await response.json();
            // Save token to localStorage for auth persistence
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            
            return data;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    },
    
    /**
     * Logout user - clear stored data
     */
    logout: () => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const logoutUrl = `${API_URL}/auth/logout?jwt_key=${encodeURIComponent(JWT_SECRET_KEY)}`;
                fetch(logoutUrl, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }).catch(e => console.log('Logout API call error:', e));
            }
        } catch (error) {
            console.error('Error during logout:', error);
        }
        
        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Clear sessionStorage as well
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        
        // Clear any cookies
        document.cookie.split(";").forEach(function(c) {
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
    },
    
    /**
     * Check if user is authenticated
     * @returns {Boolean} - True if user has valid token
     */
    isAuthenticated: () => {
        return !!localStorage.getItem('token');
    },
    
    /**
     * Get current user data
     * @returns {Object|null} - User data or null if not logged in
     */
    getCurrentUser: () => {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    },
    
    /**
     * Change user password
     * @param {String} currentPassword - Current password
     * @param {String} newPassword - New password
     * @returns {Promise<Object>} - Success message
     */
    changePassword: async (currentPassword, newPassword) => {
        try {
            const token = localStorage.getItem('token');
            
            if (!token) {
                throw new Error('Authentication required');
            }
            
            const url = `${API_URL}/auth/change-password?jwt_key=${encodeURIComponent(JWT_SECRET_KEY)}`;
            
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ currentPassword, newPassword })
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to change password');
            }
            
            return await response.json();
        } catch (error) {
            console.error('Change password error:', error);
            throw error;
        }
    },
    
    /**
     * Get user documents (salary slips)
     * @param {String} nik - User NIK
     * @param {String} [year] - Optional year filter
     * @returns {Promise<Array>} - List of documents
     */
    getDocuments: async (nik, year = null) => {
        try {
            console.log(`Getting documents for NIK: ${nik}, year: ${year || 'all'}`);
            
            const token = localStorage.getItem('token');
            console.log(`Auth token available: ${!!token}`);
            
            if (!token) {
                throw new Error('Authentication required');
            }
            
            // First check local storage for any locally stored documents
            const localSlips = localStorageUpload.getSalarySlips(nik);
            console.log(`Found ${localSlips.length} local salary slips`);
            
            try {
                // Build URL with query parameters
                let url = `${API_URL}/documents/users/${nik}/documents`;
                url += url.includes('?') ? '&' : '?';
                url += `jwt_key=${encodeURIComponent(JWT_SECRET_KEY)}`;
                
                if (year) {
                    url += `&year=${year}`;
                }
                
                console.log(`API URL: ${url}`);
                
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                console.log(`API Response status: ${response.status}`);
                
                if (!response.ok) {
                    const error = await response.json();
                    console.error('API Error:', error);
                    throw new Error(error.message || 'Failed to fetch documents');
                }
                
                const data = await response.json();
                console.log('API Response data:', data);
                
                // Combine server documents with local documents
                if (data && data.documents) {
                    // Convert local slip format to match API format
                    const formattedLocalSlips = localSlips.map(slip => ({
                        id: slip.id,
                        nik: slip.nik,
                        month: slip.month,
                        year: slip.year,
                        file_name: slip.fileName,
                        file_url: slip.fileName,
                        local: true,
                        dataUrl: slip.dataUrl
                    }));
                    
                    // Merge and filter by year if needed
                    let allDocuments = [...data.documents, ...formattedLocalSlips];
                    
                    if (year) {
                        allDocuments = allDocuments.filter(doc => doc.year == year);
                    }
                    
                    // Remove duplicates (prefer local versions)
                    const seen = new Set();
                    const uniqueDocs = allDocuments.filter(doc => {
                        const key = `${doc.nik}_${doc.month}_${doc.year}`;
                        // If we've seen this key and current doc is not local, skip it
                        if (seen.has(key) && !doc.local) return false;
                        seen.add(key);
                        return true;
                    });
                    
                    return {
                        ...data,
                        documents: uniqueDocs,
                        hasLocalDocuments: localSlips.length > 0
                    };
                }
                
                return data;
            } catch (serverError) {
                console.error('Server error getting documents:', serverError);
                
                // If server request fails, just use local documents
                if (localSlips.length > 0) {
                    const formattedLocalSlips = localSlips.map(slip => ({
                        id: slip.id,
                        nik: slip.nik,
                        month: slip.month,
                        year: slip.year,
                        file_name: slip.fileName,
                        file_url: slip.fileName,
                        local: true,
                        dataUrl: slip.dataUrl
                    }));
                    
                    // Filter by year if specified
                    let filteredSlips = formattedLocalSlips;
                    if (year) {
                        filteredSlips = formattedLocalSlips.filter(doc => doc.year == year);
                    }
                    
                    return {
                        documents: filteredSlips,
                        count: filteredSlips.length,
                        usingLocalOnly: true
                    };
                }
                
                // Re-throw if no local fallback
                throw serverError;
            }
        } catch (error) {
            console.error('Get documents error:', error);
            throw error;
        }
    },
    
    /**
     * Get user salary slips (uses getDocuments internally)
     * @returns {Promise<Array>} - List of salary slips
     */
    getSalarySlips: async () => {
        try {
            const token = localStorage.getItem('token');
            
            if (!token) {
                throw new Error('Authentication required');
            }
            
            // Get current user's NIK from localStorage
            const user = ApiService.getCurrentUser();
            if (!user || !user.nik) {
                throw new Error('User information not found');
            }
            
            // Use the getDocuments function to retrieve user's salary slips
            return await ApiService.getDocuments(user.nik);
        } catch (error) {
            console.error('Get salary slips error:', error);
            throw error;
        }
    },
    
    /**
     * Download a salary slip
     * @param {String} id - Salary slip ID
     * @returns {Promise<Blob>} - File blob
     */
    downloadSalarySlip: async (id) => {
        try {
            const token = localStorage.getItem('token');
            
            if (!token) {
                throw new Error('Authentication required');
            }
            
            const url = `${API_URL}/documents/salary-slips/${id}/download?jwt_key=${encodeURIComponent(JWT_SECRET_KEY)}`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to download salary slip');
            }
            
            return await response.blob();
        } catch (error) {
            console.error('Download salary slip error:', error);
            throw error;
        }
    },
    
    /**
     * View a salary slip (get URL)
     * @param {String} fileName - File name path in storage
     * @returns {Promise<Object>} - URL data
     */
    viewSalarySlip: async (fileName) => {
        try {
            const token = localStorage.getItem('token');
            
            if (!token) {
                throw new Error('Authentication required');
            }
            
            // Include JWT key in the URL as a query parameter for better compatibility
            const viewUrl = `https://plant.mandiriservices.biz.id/atmabahan/view-direct/?file_name=${encodeURIComponent(fileName)}&jwt_key=${encodeURIComponent(JWT_SECRET_KEY)}`;
            
            const response = await fetch(viewUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to get salary slip view URL');
            }
            
            const data = await response.json();
            
            // Open PDF in new tab
            if (data.data && data.data.url) {
                window.open(data.data.url, '_blank');
            }
            
            return data;
        } catch (error) {
            console.error('View salary slip error:', error);
            throw error;
        }
    },
    
    /**
     * Get all users (admin only)
     * @returns {Promise<Array>} - List of users
     */
    getAllUsers: async () => {
        try {
            const token = localStorage.getItem('token');
            
            if (!token) {
                throw new Error('Authentication required');
            }
            
            const url = `${API_URL}/users?jwt_key=${encodeURIComponent(JWT_SECRET_KEY)}`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to fetch users');
            }
            
            return await response.json();
        } catch (error) {
            console.error('Get users error:', error);
            throw error;
        }
    },
    
    /**
     * Add a new user (admin only)
     * @param {Object} userData - User data object
     * @returns {Promise<Object>} - Created user data
     */
    addUser: async (userData) => {
        try {
            const token = localStorage.getItem('token');
            
            if (!token) {
                throw new Error('Authentication required');
            }
            
            const url = `${API_URL}/users?jwt_key=${encodeURIComponent(JWT_SECRET_KEY)}`;
            
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(userData)
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to add user');
            }
            
            return await response.json();
        } catch (error) {
            console.error('Add user error:', error);
            throw error;
        }
    },
    
    /**
     * Update user data (admin only)
     * @param {String} id - User ID
     * @param {Object} userData - Updated user data
     * @returns {Promise<Object>} - Updated user data
     */
    updateUser: async (id, userData) => {
        try {
            const token = localStorage.getItem('token');
            
            if (!token) {
                throw new Error('Authentication required');
            }
            
            const url = `${API_URL}/users/${id}?jwt_key=${encodeURIComponent(JWT_SECRET_KEY)}`;
            
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(userData)
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update user');
            }
            
            return await response.json();
        } catch (error) {
            console.error('Update user error:', error);
            throw error;
        }
    },
    
    /**
     * Delete a user (admin only)
     * @param {String} id - User ID
     * @returns {Promise<Object>} - Success message
     */
    deleteUser: async (id) => {
        try {
            const token = localStorage.getItem('token');
            
            if (!token) {
                throw new Error('Authentication required');
            }
            
            const url = `${API_URL}/users/${id}?jwt_key=${encodeURIComponent(JWT_SECRET_KEY)}`;
            
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to delete user');
            }
            
            return await response.json();
        } catch (error) {
            console.error('Delete user error:', error);
            throw error;
        }
    },
    
    /**
     * Upload a salary slip (admin only)
     * @param {FormData} formData - Form data with file and details
     * @returns {Promise<Object>} - Upload result
     */
    uploadSalarySlip: async (formData) => {
        try {
            console.log('Uploading salary slip with formData:', formData);
            
            const token = localStorage.getItem('token');
            
            if (!token) {
                throw new Error('Authentication required');
            }
            
            // For FormData, we need to ensure all required fields are included
            const formValues = {};
            for (let [key, value] of formData.entries()) {
                formValues[key] = value;
                console.log(`FormData field: ${key} = ${value instanceof File ? value.name : value}`);
            }
            
            // Check if FormData has the necessary fields
            if (!formData.has('file')) {
                console.error('Missing file in FormData');
                throw new Error('File is required for upload');
            }
            
            // Extract key data for potential local storage fallback
            const file = formData.get('file');
            const nik = formData.get('nik');
            const month = formData.get('month');
            const year = formData.get('year');
            const additionalData = {
                updateExistingUsers: formData.get('updateExistingUsers'),
                handleDuplicates: formData.get('handleDuplicates')
            };
            
            // Try server upload first
            try {
                // Use the correct upload endpoint
                const uploadUrl = `${API_URL}/upload/slip-gaji?jwt_key=${encodeURIComponent(JWT_SECRET_KEY)}`;
                
                // Attach authentication directly in headers
                const headers = {
                    'Authorization': `Bearer ${token}`
                };
                
                // Implement fetch with timeout and retry logic
                const fetchWithTimeout = (url, options, timeout = 30000) => {
                    return Promise.race([
                        fetch(url, options),
                        new Promise((_, reject) => 
                            setTimeout(() => reject(new Error('Request timeout')), timeout)
                        )
                    ]);
                };
                
                // Try upload with retry logic (3 attempts)
                let response;
                let attempts = 0;
                const maxAttempts = 3;
                
                while (attempts < maxAttempts) {
                    attempts++;
                    
                    try {
                        console.log(`Upload attempt ${attempts}/${maxAttempts}`);
                        response = await fetchWithTimeout(uploadUrl, {
                            method: 'POST',
                            headers: headers,
                            body: formData
                        }, 60000); // 60 second timeout
                        
                        // If successful, break the loop
                        break;
                    } catch (fetchError) {
                        console.error(`Attempt ${attempts} failed:`, fetchError);
                        
                        // On last attempt, we'll try local storage instead of throwing an error
                        if (attempts === maxAttempts) {
                            console.log('All server attempts failed, falling back to local storage');
                            throw fetchError; // This will be caught by outer try/catch
                        }
                        
                        // Wait before retrying (exponential backoff)
                        const delay = Math.pow(2, attempts) * 1000;
                        console.log(`Retrying in ${delay}ms...`);
                        await new Promise(resolve => setTimeout(resolve, delay));
                    }
                }
                
                // If response is not ok, handle different error formats
                if (!response.ok) {
                    const statusCode = response.status;
                    let errorMessage;
                    
                    try {
                        // Try to parse as JSON
                        const errorData = await response.json();
                        errorMessage = errorData.message || `Server error: ${statusCode}`;
                    } catch (jsonError) {
                        // If not JSON, handle based on status code
                        if (statusCode === 500) {
                            errorMessage = 'Server error (500): The server encountered an internal error.';
                        } else if (statusCode === 404) {
                            errorMessage = 'Server error (404): API endpoint tidak ditemukan. Silakan hubungi administrator.';
                        } else {
                            errorMessage = `Upload failed with status: ${statusCode}`;
                        }
                    }
                    
                    throw new Error(errorMessage);
                }
                
                // For successful responses
                try {
                    // Try to parse JSON response
                    const result = await response.json();
                    console.log('Upload success response:', result);
                    return result;
                } catch (jsonError) {
                    // If successful but not JSON, return basic success object
                    console.log('Upload successful but response not JSON');
                    return {
                        success: true,
                        message: 'File uploaded successfully',
                        status: response.status
                    };
                }
            } catch (serverError) {
                // Server upload failed, try local storage
                console.log('Server upload failed, using local storage instead:', serverError);
                
                // Use our local storage implementation
                const localResult = await localStorageUpload.uploadSalarySlip(
                    file, nik, month, year, additionalData
                );
                
                console.log('Local storage upload result:', localResult);
                
                // Add metadata about the fallback
                return {
                    ...localResult,
                    usingFallback: true,
                    originalError: serverError.message
                };
            }
        } catch (error) {
            console.error('Upload salary slip error:', error);
            throw error;
        }
    }
};

export default ApiService; 